/**
 * billingService.ts — Google Play Billing
 * Todos los métodos de consulta capturan errores y devuelven valores seguros.
 * Solo launchPurchaseFlow relanza el error porque el usuario lo inició intencionalmente.
 */

import { Capacitor, registerPlugin, PluginListenerHandle } from '@capacitor/core';

// ── IDs de productos según estructura en Play Console ──
export const BILLING_PRODUCT_IDS = {
  PREMIUM_PERSONAL: 'premium_personal',
  PREMIUM_PRO: 'premium_pro',
} as const;

export const BILLING_BASE_PLAN_IDS = {
  PREMIUM_PRO:      'pro-monthly',
  PREMIUM_PERSONAL: 'personal-monthly',
} as const;

export const BILLING_OFFER_ID_PRO_TRIAL      = 'pro-free-trial-1m';
export const BILLING_OFFER_ID_PERSONAL_TRIAL = 'personal-free-trial-1m';

// ── Mensaje de error para UI en español ──
export const BILLING_UNAVAILABLE_MESSAGE =
  'No hemos podido conectar con Google Play. Revisa que Play Store esté actualizado o inténtalo más tarde.';

// ── Tipos ──
export interface BillingProduct {
  productId: string;
  name: string;
  basePlanId: string;
  offerId: string;       // "" si es base plan, "pro-free-trial-1m" si es oferta trial
  offerToken: string;
  offerTags: string;     // comma-separated
  formattedPrice: string;
}

export interface ActivePurchase {
  orderId: string;
  products: string;
  purchaseState: number;
  isAcknowledged: boolean;
  purchaseToken?: string;  // presente en eventos de onPurchasesUpdated
}

export interface PurchasesUpdatedEvent {
  responseCode: number;
  purchases: ActivePurchase[];
}

export interface BillingInitResult {
  connected: boolean;
  responseCode: number;
}

// ── Interfaz del plugin nativo ──
interface BillingPluginInterface {
  initialize(): Promise<BillingInitResult>;
  queryProducts(): Promise<{ products: BillingProduct[] }>;
  queryPurchases(): Promise<{ purchases: ActivePurchase[] }>;
  launchPurchase(opts: { productId: string; offerToken: string }): Promise<void>;
  addListener(
    event: 'purchasesUpdated',
    listenerFunc: (data: PurchasesUpdatedEvent) => void
  ): Promise<PluginListenerHandle>;
}

// ── Registro del plugin via patrón estándar de Capacitor ──
const BillingPlugin = registerPlugin<BillingPluginInterface>('BillingPlugin');

// ── Guard de plataforma ──
function isNativeAndroid(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
}

// ── Inicializar BillingClient ──
// Nunca lanza error al UI: si falla devuelve { connected: false, responseCode: -1 }
export async function initBillingClient(): Promise<BillingInitResult> {
  if (!isNativeAndroid()) {
    console.log('[BILLING] Not native Android — skipping BillingClient init');
    return { connected: false, responseCode: -1 };
  }
  try {
    const result = await BillingPlugin.initialize();
    if (result.connected) {
      console.log('[BILLING] BillingClient initialized OK');
    } else {
      console.warn('[BILLING] BillingClient connected=false, responseCode:', result.responseCode);
    }
    return result;
  } catch (err) {
    console.error('[BILLING] Init error (non-blocking):', err);
    return { connected: false, responseCode: -1 };
  }
}

// ── Consultar productos de suscripción disponibles ──
// Si falla, devuelve [] sin lanzar error al UI
export async function querySubscriptionProducts(): Promise<BillingProduct[]> {
  if (!isNativeAndroid()) return [];
  try {
    const { products } = await BillingPlugin.queryProducts();
    const list = Array.isArray(products) ? products : [];
    console.log('[BILLING] Products fetched:', list.length);
    return list;
  } catch (err) {
    console.warn('[BILLING] queryProducts failed, returning empty list:', err);
    return [];
  }
}

// ── Consultar compras activas del usuario ──
// Si falla, devuelve [] sin lanzar error al UI
export async function queryActivePurchases(): Promise<ActivePurchase[]> {
  if (!isNativeAndroid()) return [];
  try {
    const { purchases } = await BillingPlugin.queryPurchases();
    const list = Array.isArray(purchases) ? purchases : [];
    console.log('[BILLING] Active purchases:', list.length);
    console.log('[BILLING TEST] purchaseToken:', list[0]?.purchaseToken || 'NO_TOKEN');
    return list;
  } catch (err) {
    console.warn('[BILLING] queryPurchases failed, returning empty list:', err);
    return [];
  }
}

// ── Lanzar flujo de compra ──
// Este SÍ relanza el error: el usuario lo inició intencionalmente y el modal lo captura
export async function launchPurchaseFlow(productId: string, offerToken: string): Promise<void> {
  if (!isNativeAndroid()) {
    throw new Error('[BILLING] launchPurchaseFlow solo disponible en Android nativo');
  }
  try {
    await BillingPlugin.launchPurchase({ productId, offerToken });
    console.log('[BILLING] launchPurchase resolved — esperando onPurchasesUpdated');
  } catch (err) {
    console.error('[BILLING] launchPurchase error:', err);
    throw err; // Relanzar: el modal de suscripciones lo maneja con mensaje al usuario
  }
}

// ── Registrar listener de compras ──
export async function addPurchaseListener(
  callback: (data: PurchasesUpdatedEvent) => void
): Promise<PluginListenerHandle | null> {
  if (!isNativeAndroid()) return null;
  try {
    return await BillingPlugin.addListener('purchasesUpdated', callback);
  } catch (err) {
    console.error('[BILLING] addPurchaseListener error:', err);
    return null;
  }
}

// ── Mapear compra activa al plan de suscripción ──
export function mapPurchaseToPlan(purchase: ActivePurchase): string | null {
  if (purchase.purchaseState !== 1) return null; // Solo PURCHASED (1)
  const products = purchase.products.split(',');
  if (products.includes(BILLING_PRODUCT_IDS.PREMIUM_PRO))      return 'premium_pro';
  if (products.includes(BILLING_PRODUCT_IDS.PREMIUM_PERSONAL)) return 'premium_personal';
  return null;
}

// ── Restaurar compras: consulta y devuelve el plan derivado ──
export async function restorePurchases(): Promise<string> {
  const purchases = await queryActivePurchases();
  for (const p of purchases) {
    const plan = mapPurchaseToPlan(p);
    if (plan) return plan;
  }
  return 'free';
}
